import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Phone, Briefcase, FileText, Bell, CheckCircle, AlertCircle, Camera, Trash2, CreditCard, Building, MapPin, DollarSign, Shield } from 'lucide-react';
import api from '../services/api';
import SpaceBackground from '../components/layout/SpaceBackground';

const DEPARTMENTS = [
  'Legal',
  'Administration',
  'Accounting',
  'Human Resources',
  'Marketing',
  'IT',
  'Operations',
];

const FILING_STATUSES = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'MARRIED', label: 'Married Filing Jointly' },
  { value: 'MARRIED_SEPARATE', label: 'Married Filing Separately' },
  { value: 'HEAD_OF_HOUSEHOLD', label: 'Head of Household' },
];

const US_STATES = [
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'AL', label: 'Alabama' },
  { value: 'TX', label: 'Texas' },
  { value: 'CA', label: 'California' },
  { value: 'NY', label: 'New York' },
  // Add more states as needed
];

const Profile = () => {
  const { user, profileStatus, refreshProfileStatus } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Profile fields
  const [profile, setProfile] = useState({
    avatar_url: '',
    full_name: '',
    display_name: '',
    email: '',
    phone: '',
    title: '',
    bio: '',
    department: '',
    office_location: '',
    notifications_email: true,
    notifications_sms: false,
    notifications_desktop: true,
  });

  // Payroll fields
  const [payroll, setPayroll] = useState({
    ssn: '',
    has_ssn: false,
    ssn_last_four: '',
    filing_status: '',
    federal_allowances: 0,
    w4_step2_checkbox: false,
    w4_dependents_amount: 0,
    w4_other_income: 0,
    w4_deductions: 0,
    w4_extra_withholding: 0,
    state: 'FL',
    bank_name: '',
    bank_routing: '',
    bank_routing_last_four: '',
    bank_account: '',
    has_bank_account: false,
    bank_account_type: 'CHECKING',
  });

  useEffect(() => {
    loadProfile();
    loadPayrollInfo();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get('/users/me/profile');
      const data = response.data;
      setProfile({
        avatar_url: data.avatar_url || '',
        full_name: data.full_name || '',
        display_name: data.display_name || user?.username || '',
        email: data.email || user?.email || '',
        phone: data.phone || '',
        title: data.title || '',
        bio: data.bio || '',
        department: data.department || '',
        office_location: data.office_location || '',
        notifications_email: data.notifications_email ?? true,
        notifications_sms: data.notifications_sms ?? false,
        notifications_desktop: data.notifications_desktop ?? true,
      });
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const loadPayrollInfo = async () => {
    try {
      const response = await api.get('/users/me/payroll-info');
      const data = response.data;
      setPayroll(prev => ({
        ...prev,
        has_ssn: data.has_ssn || false,
        ssn_last_four: data.ssn_last_four || '',
        filing_status: data.filing_status || '',
        federal_allowances: data.federal_allowances || 0,
        w4_step2_checkbox: data.w4_step2_checkbox || false,
        w4_dependents_amount: data.w4_dependents_amount || 0,
        w4_other_income: data.w4_other_income || 0,
        w4_deductions: data.w4_deductions || 0,
        w4_extra_withholding: data.w4_extra_withholding || 0,
        state: data.state || 'FL',
        bank_name: data.bank_name || '',
        bank_routing_last_four: data.bank_routing_last_four || '',
        has_bank_account: data.has_bank_account || false,
        bank_account_type: data.bank_account_type || 'CHECKING',
      }));
    } catch (err) {
      console.error('Failed to load payroll info:', err);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/users/me/profile', {
        full_name: profile.full_name,
        display_name: profile.display_name,
        email: profile.email,
        phone: profile.phone,
        title: profile.title,
        bio: profile.bio,
        department: profile.department,
        office_location: profile.office_location,
        notifications_email: profile.notifications_email,
        notifications_sms: profile.notifications_sms,
        notifications_desktop: profile.notifications_desktop,
      });

      setSuccess('Profile updated successfully');
      await refreshProfileStatus();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePayroll = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payrollData = {};

      // Only send SSN if user entered a new one
      if (payroll.ssn && payroll.ssn.length >= 9) {
        payrollData.ssn = payroll.ssn.replace(/[^0-9]/g, '');
      }

      if (payroll.filing_status) {
        payrollData.filing_status = payroll.filing_status;
      }

      payrollData.federal_allowances = payroll.federal_allowances;
      payrollData.w4_step2_checkbox = payroll.w4_step2_checkbox;
      payrollData.w4_dependents_amount = payroll.w4_dependents_amount;
      payrollData.w4_other_income = payroll.w4_other_income;
      payrollData.w4_deductions = payroll.w4_deductions;
      payrollData.w4_extra_withholding = payroll.w4_extra_withholding;
      payrollData.state = payroll.state;

      if (payroll.bank_name) {
        payrollData.bank_name = payroll.bank_name;
      }

      // Only send routing if user entered a new one
      if (payroll.bank_routing && payroll.bank_routing.length >= 9) {
        payrollData.bank_routing = payroll.bank_routing;
      }

      // Only send account if user entered a new one
      if (payroll.bank_account && payroll.bank_account.length >= 4) {
        payrollData.bank_account = payroll.bank_account;
      }

      if (payroll.bank_account_type) {
        payrollData.bank_account_type = payroll.bank_account_type;
      }

      await api.put('/users/me/payroll-info', payrollData);

      setSuccess('Payroll information updated successfully');
      await loadPayrollInfo();
      await refreshProfileStatus();
      // Clear sensitive fields after save
      setPayroll(prev => ({ ...prev, ssn: '', bank_routing: '', bank_account: '' }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save payroll information');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handlePayrollChange = (field, value) => {
    setPayroll(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP.');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError('File too large. Maximum size is 20MB.');
      return;
    }

    setAvatarLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfile(prev => ({ ...prev, avatar_url: response.data.avatar_url }));
      setSuccess('Avatar uploaded successfully');
      await refreshProfileStatus();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setAvatarLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAvatarDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your avatar?')) return;

    setAvatarLoading(true);
    setError('');

    try {
      await api.delete('/users/me/avatar');
      setProfile(prev => ({ ...prev, avatar_url: '' }));
      setSuccess('Avatar deleted');
      await refreshProfileStatus();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete avatar');
    } finally {
      setAvatarLoading(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'contact', label: 'Contact', icon: Mail },
    { id: 'payroll', label: 'Payroll', icon: CreditCard },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  const getFieldStatus = (field) => {
    const missingProfile = profileStatus?.missing_profile_fields || [];
    const missingPayroll = profileStatus?.missing_payroll_fields || [];
    if (missingProfile.includes(field) || missingPayroll.includes(field)) {
      return 'missing';
    }
    return 'complete';
  };

  const renderRequiredBadge = (field) => {
    const status = getFieldStatus(field);
    if (status === 'missing') {
      return <span className="ml-2 text-xs text-red-400">Required</span>;
    }
    return <span className="ml-2 text-xs text-green-400"><CheckCircle className="w-3 h-3 inline" /></span>;
  };

  return (
    <div className="max-w-4xl mx-auto relative">
      <SpaceBackground />
      <div className="relative z-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-virgo-teal-100">My Profile</h1>
        {profileStatus && (
          <div className="flex items-center space-x-2">
            <div className="text-sm text-virgo-teal-300">
              {profileStatus.profile_completion_percentage}% Complete
            </div>
            <div className="w-24 h-2 bg-virgo-navy-600 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  profileStatus.overall_complete ? 'bg-green-500' : 'bg-amber-500'
                }`}
                style={{ width: `${profileStatus.profile_completion_percentage}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-500 text-red-200 rounded flex items-center">
          <AlertCircle size={20} className="mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-900/50 border border-green-500 text-green-200 rounded flex items-center">
          <CheckCircle size={20} className="mr-2 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Avatar Section */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-6">
          <div className="relative">
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-virgo-teal-600"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-virgo-teal-500 to-virgo-teal-600 flex items-center justify-center text-white text-3xl font-bold">
                {(profile.full_name || profile.display_name || user?.username || 'U').charAt(0).toUpperCase()}
              </div>
            )}

            <div className="absolute -bottom-2 -right-2 flex space-x-1">
              <button
                onClick={handleAvatarClick}
                disabled={avatarLoading}
                className="bg-virgo-teal-600 text-white p-2 rounded-full hover:bg-virgo-teal-700 transition shadow-lg disabled:bg-gray-500"
                title="Upload photo"
              >
                <Camera size={16} />
              </button>
              {profile.avatar_url && (
                <button
                  onClick={handleAvatarDelete}
                  disabled={avatarLoading}
                  className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition shadow-lg disabled:bg-gray-500"
                  title="Delete photo"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleAvatarUpload}
              className="hidden"
            />

            {avatarLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          <div className="flex-1">
            <h2 className="text-xl font-semibold text-virgo-teal-100">
              {profile.full_name || profile.display_name || user?.username}
            </h2>
            <p className="text-virgo-teal-300">{profile.title || 'No title set'}</p>
            <span className="inline-block mt-2 bg-virgo-teal-600/30 text-virgo-teal-200 px-3 py-1 rounded-full text-sm font-medium capitalize">
              {user?.role}
            </span>
            {!profile.avatar_url && (
              <p className="text-amber-400 text-sm mt-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Profile photo required for About Us page
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-virgo-navy-700 rounded-lg shadow mb-6">
        <div className="border-b border-virgo-navy-600">
          <nav className="flex space-x-1 p-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    isActive
                      ? 'bg-virgo-teal-600 text-white'
                      : 'text-virgo-teal-300 hover:bg-virgo-navy-600'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Personal Tab */}
          {activeTab === 'personal' && (
            <div className="space-y-4">
              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Full Name {renderRequiredBadge('full_name')}
                </label>
                <input
                  type="text"
                  value={profile.full_name}
                  onChange={(e) => handleProfileChange('full_name', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="Your full legal name"
                />
              </div>

              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={profile.display_name}
                  onChange={(e) => handleProfileChange('display_name', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="How you want to be displayed"
                />
              </div>

              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Job Title {renderRequiredBadge('title')}
                </label>
                <input
                  type="text"
                  value={profile.title}
                  onChange={(e) => handleProfileChange('title', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="e.g., Associate Attorney, Paralegal"
                />
              </div>

              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Bio {renderRequiredBadge('bio')}
                </label>
                <textarea
                  value={profile.bio}
                  onChange={(e) => handleProfileChange('bio', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="A brief professional bio for the About Us page..."
                />
                <p className="text-virgo-teal-400 text-sm mt-1">This will appear on the public website.</p>
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="mt-4 bg-virgo-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition disabled:bg-gray-500"
              >
                {loading ? 'Saving...' : 'Save Personal Info'}
              </button>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Phone Number {renderRequiredBadge('phone')}
                </label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handleProfileChange('phone', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Department {renderRequiredBadge('department')}
                </label>
                <select
                  value={profile.department}
                  onChange={(e) => handleProfileChange('department', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Select department...</option>
                  {DEPARTMENTS.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                  Office Location
                </label>
                <input
                  type="text"
                  value={profile.office_location}
                  onChange={(e) => handleProfileChange('office_location', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="e.g., Main Office, Remote"
                />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="mt-4 bg-virgo-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition disabled:bg-gray-500"
              >
                {loading ? 'Saving...' : 'Save Contact Info'}
              </button>
            </div>
          )}

          {/* Payroll Tab */}
          {activeTab === 'payroll' && (
            <div className="space-y-6">
              <div className="bg-virgo-navy-600/50 border border-virgo-navy-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-virgo-teal-400 mt-0.5" />
                  <div>
                    <h3 className="text-virgo-teal-200 font-medium">Secure Information</h3>
                    <p className="text-virgo-teal-400 text-sm">
                      Your SSN and bank account information is encrypted and stored securely.
                      This information is required for payroll processing.
                    </p>
                  </div>
                </div>
              </div>

              {/* Tax Information */}
              <div>
                <h3 className="text-lg font-semibold text-virgo-teal-100 mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Tax Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      Social Security Number {renderRequiredBadge('ssn')}
                    </label>
                    {payroll.has_ssn ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={payroll.ssn || `***-**-${payroll.ssn_last_four}`}
                          onChange={(e) => handlePayrollChange('ssn', e.target.value)}
                          className="flex-1 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                          placeholder="Enter new SSN to update"
                        />
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={payroll.ssn}
                        onChange={(e) => handlePayrollChange('ssn', e.target.value)}
                        className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                        placeholder="XXX-XX-XXXX"
                        maxLength={11}
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      Filing Status {renderRequiredBadge('filing_status')}
                    </label>
                    <select
                      value={payroll.filing_status}
                      onChange={(e) => handlePayrollChange('filing_status', e.target.value)}
                      className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    >
                      <option value="">Select status...</option>
                      {FILING_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      State
                    </label>
                    <select
                      value={payroll.state}
                      onChange={(e) => handlePayrollChange('state', e.target.value)}
                      className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    >
                      {US_STATES.map(state => (
                        <option key={state.value} value={state.value}>{state.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      Extra Withholding (per pay period)
                    </label>
                    <input
                      type="number"
                      value={payroll.w4_extra_withholding}
                      onChange={(e) => handlePayrollChange('w4_extra_withholding', parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Information */}
              <div>
                <h3 className="text-lg font-semibold text-virgo-teal-100 mb-4 flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  Direct Deposit Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      Bank Name {renderRequiredBadge('bank_name')}
                    </label>
                    <input
                      type="text"
                      value={payroll.bank_name}
                      onChange={(e) => handlePayrollChange('bank_name', e.target.value)}
                      className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                      placeholder="Bank of America, Chase, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      Account Type
                    </label>
                    <select
                      value={payroll.bank_account_type}
                      onChange={(e) => handlePayrollChange('bank_account_type', e.target.value)}
                      className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    >
                      <option value="CHECKING">Checking</option>
                      <option value="SAVINGS">Savings</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      Routing Number {renderRequiredBadge('bank_routing')}
                    </label>
                    {payroll.bank_routing_last_four ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={payroll.bank_routing || `*****${payroll.bank_routing_last_four}`}
                          onChange={(e) => handlePayrollChange('bank_routing', e.target.value)}
                          className="flex-1 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                          placeholder="Enter new routing to update"
                        />
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={payroll.bank_routing}
                        onChange={(e) => handlePayrollChange('bank_routing', e.target.value)}
                        className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                        placeholder="9 digit routing number"
                        maxLength={9}
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-virgo-teal-300 text-sm font-medium mb-1">
                      Account Number {renderRequiredBadge('bank_account')}
                    </label>
                    {payroll.has_bank_account ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={payroll.bank_account || '********'}
                          onChange={(e) => handlePayrollChange('bank_account', e.target.value)}
                          className="flex-1 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                          placeholder="Enter new account to update"
                        />
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={payroll.bank_account}
                        onChange={(e) => handlePayrollChange('bank_account', e.target.value)}
                        className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                        placeholder="Your account number"
                      />
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={handleSavePayroll}
                disabled={loading}
                className="mt-4 bg-virgo-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition disabled:bg-gray-500"
              >
                {loading ? 'Saving...' : 'Save Payroll Information'}
              </button>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile.notifications_email}
                  onChange={(e) => handleProfileChange('notifications_email', e.target.checked)}
                  className="w-5 h-5 text-virgo-teal-500 border-virgo-navy-500 rounded focus:ring-virgo-teal-500 bg-virgo-navy-600"
                />
                <span className="text-virgo-teal-200">Email notifications</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile.notifications_sms}
                  onChange={(e) => handleProfileChange('notifications_sms', e.target.checked)}
                  className="w-5 h-5 text-virgo-teal-500 border-virgo-navy-500 rounded focus:ring-virgo-teal-500 bg-virgo-navy-600"
                />
                <span className="text-virgo-teal-200">SMS notifications</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile.notifications_desktop}
                  onChange={(e) => handleProfileChange('notifications_desktop', e.target.checked)}
                  className="w-5 h-5 text-virgo-teal-500 border-virgo-navy-500 rounded focus:ring-virgo-teal-500 bg-virgo-navy-600"
                />
                <span className="text-virgo-teal-200">Desktop notifications</span>
              </label>

              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="mt-4 bg-virgo-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition disabled:bg-gray-500"
              >
                {loading ? 'Saving...' : 'Save Notification Preferences'}
              </button>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Profile;
